<template>
    <Teleport to="body">
        <Transition name="sidebar-fade">
            <button v-if="goal" type="button" class="fixed inset-0 z-30 w-full cursor-default bg-black/40 md:hidden" aria-label="Close goal details" @click="emit('close')" />
        </Transition>
        <Transition name="sidebar-slide">
            <div
                v-if="goal"
                data-dismiss-ignore
                class="fixed inset-x-0 bottom-0 z-40 flex w-full max-h-[90dvh] flex-col rounded-t-2xl border-t border-[#e2e8f0] bg-[#fafafa] shadow-2xl pb-[var(--mobile-nav-total-height,0px)] md:inset-y-0 md:right-0 md:left-auto md:w-[440px] md:max-h-none md:rounded-none md:border-t-0 md:border-l md:pb-0"
                role="dialog"
                aria-modal="true">
                <!-- Completion confirmation overlay -->
                <SidebarConfirmOverlay
                    v-model:open="showCompleteConfirm"
                    title="Mark this goal as completed?"
                    confirm-text="Complete Goal"
                    :is-loading="isCompleting"
                    :error-text="completeErrorText"
                    @update:open="handleCompleteOverlayVisibility"
                    @confirmed="handleMarkComplete" />

                <!-- Connection overlay for unassigned team goals -->
                <div v-if="connectionOverlayOpen" class="absolute inset-0 z-50 flex items-center justify-center bg-black/30 px-6">
                    <div class="w-full rounded-2xl bg-[#fafafa] p-6 flex flex-col gap-4">
                        <BaseNotification v-if="connectionAssignErrorText" theme="error" :text="connectionAssignErrorText" hide-dismiss />
                        <p class="body-semibold text-valence-grey-900">Assign this goal to a team member</p>
                        <ConnectionSearch ref="connectionSearchRef" auto-focus @select="selectedConnection = $event" @clear="selectedConnection = null" />
                        <div class="flex items-center justify-end gap-3">
                            <BaseButton theme="neutral" size="sm" :disabled="isAssigningConnection" @click="connectionOverlayOpen = false">Cancel</BaseButton>
                            <BaseButton theme="primary" size="sm" :disabled="!selectedConnection || isAssigningConnection" @click="submitConnectionAssign">Save</BaseButton>
                        </div>
                    </div>
                </div>

                <!-- Delete feedback overlay -->
                <div v-if="showDeleteFeedback" class="absolute inset-0 z-50 flex items-center justify-center bg-black/30 px-6">
                    <div class="w-full rounded-2xl bg-[#fafafa] p-6 flex flex-col gap-4">
                        <BaseNotification v-if="deleteErrorText" theme="error" :text="deleteErrorText" hide-dismiss />
                        <p class="body-semibold text-valence-grey-900">Why are you deleting this goal?</p>
                        <DiscardGoalFeedback :goal-id="goal.id" @discarded="handleDelete" @cancel="showDeleteFeedback = false" />
                    </div>
                </div>

                <!-- Header -->
                <div class="flex items-center justify-between gap-3 px-6 py-5 border-b border-[#e2e8f0]">
                    <div class="flex items-center gap-2 min-w-0">
                        <h2 class="body text-valence-grey-900 min-w-0">{{ goal.title }}</h2>
                    </div>
                    <button
                        type="button"
                        class="shrink-0 flex h-6 w-6 items-center justify-center rounded text-valence-grey-500 hover:bg-slate-100"
                        aria-label="Close"
                        @click="emit('close')">
                        <i class="bi bi-x-lg text-sm" aria-hidden="true"></i>
                    </button>
                </div>

                <!-- Body -->
                <div class="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
                    <!-- Date -->
                    <div class="flex items-center gap-2">
                        <p class="body-small text-valence-grey-500">Added on {{ fullDateLabel }}</p>
                    </div>

                    <!-- Description -->
                    <div>
                        <p v-if="goal.description" class="details-big text-valence-grey-900">{{ goal.description }}</p>
                        <p v-else class="details-big italic text-valence-grey-400">No description added.</p>
                    </div>

                    <!-- Actions -->
                    <GoalActionsSection
                        ref="actionsRef"
                        :goal-id="goal.id"
                        :actions="goal.actions"
                        :allow-add-action="!disableActions"
                        @changed="emit('action-added', goal.id)"
                        @all-completed="showCompleteConfirm = true"
                        @request-delete-confirm="pendingDeleteActionId = $event" />

                    <!-- Delete action confirmation overlay -->
                    <div v-if="pendingDeleteActionId" class="absolute inset-0 z-50 flex items-center justify-center bg-black/30 px-6">
                        <div class="w-full rounded-2xl bg-[#fafafa] p-6 flex flex-col gap-4">
                            <BaseNotification v-if="deleteActionErrorText" theme="error" :text="deleteActionErrorText" hide-dismiss />
                            <p class="text-[15px] font-medium text-valence-grey-900">Delete this action?</p>
                            <p class="body-small text-valence-grey-700">This cannot be undone.</p>
                            <div class="flex justify-end gap-2">
                                <BaseButton theme="neutral" size="sm" :disabled="isDeletingAction" @click="pendingDeleteActionId = null">Cancel</BaseButton>
                                <BaseButton theme="primary" size="sm" :disabled="isDeletingAction" @click="handleConfirmDeleteAction">
                                    {{ isDeletingAction ? "Deleting…" : "Delete" }}
                                </BaseButton>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Priority button -->
                <div v-if="showPriorityButton" class="px-6 pb-4">
                    <GoalPriorityToggleButton :is-prioritized="!!goal.prioritized_at" :disabled="isPriorityButtonDisabled" @click="emit('toggle-priority', goal.id)" />
                </div>

                <!-- Standard footer -->
                <div v-if="!hideFooter" class="border-t border-[#e2e8f0]">
                    <div class="flex items-center gap-3 px-6 py-4">
                        <BaseButton
                            v-if="isSuggestedGoal(goal) && !goal.team_member_goal && currentTab === 'for-you'"
                            theme="primary"
                            size="md"
                            class="md:hidden ml-auto"
                            @click="emit('track-suggestion', goal.id)">
                            Track this goal
                        </BaseButton>
                        <BaseButton theme="secondary" size="md" :disabled="isFetchingChatUrl" @click="goToChat">
                            <span class="relative inline-flex items-center justify-center">
                                <LoadingSpinner v-if="isFetchingChatUrl" class="h-4 w-4 absolute" />
                                <span :class="isFetchingChatUrl ? 'invisible' : ''">Dive Deeper</span>
                            </span>
                        </BaseButton>
                        <div class="ml-auto">
                            <BaseDropdownMenu
                                :sections="moreMenuSections"
                                side="top"
                                :side-offset="4"
                                @select="handleMoreMenuSelect"
                                @close-auto-focus="handleMoreMenuCloseAutoFocus">
                                <template #trigger>
                                    <button
                                        type="button"
                                        class="inline-flex items-center justify-center rounded-full border border-valence-grey-300 px-3 py-1.5 body-small text-valence-grey-600 hover:bg-slate-50"
                                        aria-label="More actions"
                                        @click="handleMoreMenuOpened">
                                        <i class="bi bi-three-dots text-sm" aria-hidden="true"></i>
                                    </button>
                                </template>
                            </BaseDropdownMenu>
                        </div>
                    </div>
                </div>

                <!-- Completed at banner -->
                <div v-if="goal.completed_at" class="border-t border-[#e2e8f0] px-6 py-3 flex items-center gap-2">
                    <i class="bi bi-check-circle-fill text-sm text-valence-primary-500" aria-hidden="true"></i>
                    <p class="body-small text-valence-grey-600">Completed {{ completedAtLabel }}</p>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { GoalUpdateRequest } from "~vue/api/generated/goals";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { type Goal } from "~vue/types/goals";
import { isSuggestedGoal } from "~vue/utils/goals";
import { computed, ref, watch } from "vue";

import ConnectionSearch from "./ConnectionSearch.vue";
import DiscardGoalFeedback from "./DiscardGoalFeedback.vue";
import GoalActionsSection from "./GoalActionsSection.vue";
import GoalPriorityToggleButton from "./GoalPriorityToggleButton.vue";
import SidebarConfirmOverlay from "./SidebarConfirmOverlay.vue";
import { type DiscussWithNadiaClickedExtraData, JourneysAmplitudeEventNames, type JourneysTab, logJourneysUserInteraction } from "./utils";

const props = defineProps<{
    goal: Goal | null;
    canPrioritize?: boolean;
    hidePriorityButton?: boolean;
    hideFooter?: boolean;
    disableActions?: boolean;
    currentTab?: JourneysTab;
}>();

const emit = defineEmits<{
    close: [];
    "action-added": [goalId: string];
    "goal-completed": [goalId: string];
    "goal-deleted": [goalId: string];
    "goal-updated": [goalId: string];
    "goal-assigned": [goalId: string];
    "goal-confirmed": [goalId: string];
    "toggle-priority": [goalId: string];
    "track-suggestion": [goalId: string];
    edit: [goalId: string];
}>();

const actionsRef = ref<InstanceType<typeof GoalActionsSection> | null>(null);
const pendingDeleteActionId = ref<string | null>(null);
const isDeletingAction = ref(false);
const deleteActionErrorText = ref("");

async function handleConfirmDeleteAction() {
    if (!pendingDeleteActionId.value) {
        return;
    }
    isDeletingAction.value = true;
    deleteActionErrorText.value = "";
    try {
        await actionsRef.value?.executeDelete(pendingDeleteActionId.value);
        pendingDeleteActionId.value = null;
    } catch {
        deleteActionErrorText.value = "Couldn't delete action. Please try again.";
    } finally {
        isDeletingAction.value = false;
    }
}

const showCompleteConfirm = ref(false);
const isCompleting = ref(false);
const showDeleteFeedback = ref(false);
const isDeleting = ref(false);

const { execute: completeGoal, error: completeError } = useFetch(
    computed(() => `/v1/goals/${props.goal?.id}`),
    { immediate: false },
)
    .patch({ status: "completed" })
    .json<Goal>();

const { execute: deleteGoal, error: deleteError } = useFetch(
    computed(() => `/v1/goals/${props.goal?.id}`),
    { immediate: false },
).delete();

const completeErrorText = ref("");
const deleteErrorText = ref("");

function handleCompleteOverlayVisibility(open: boolean) {
    if (!open) {
        showCompleteConfirm.value = false;
        completeErrorText.value = "";
    }
}

async function handleMarkComplete() {
    isCompleting.value = true;
    completeErrorText.value = "";
    await completeGoal();
    isCompleting.value = false;

    if (completeError.value) {
        completeErrorText.value = "Couldn't complete goal. Please try again.";
        return;
    }

    showCompleteConfirm.value = false;
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_COMPLETED, { goal_id: props.goal!.id, was_prioritized: !!props.goal!.prioritized_at }, props.currentTab);
    emit("goal-completed", props.goal!.id);
    emit("close");
}

async function handleDelete() {
    isDeleting.value = true;
    deleteErrorText.value = "";
    await deleteGoal();
    isDeleting.value = false;

    if (deleteError.value) {
        deleteErrorText.value = "Couldn't delete goal. Please try again.";
        return;
    }

    showDeleteFeedback.value = false;
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_DISCARDED, { goal_id: props.goal!.id }, props.currentTab);
    emit("goal-deleted", props.goal!.id);
    emit("close");
}

const connectionOverlayOpen = ref(false);
const connectionSearchRef = ref<InstanceType<typeof ConnectionSearch>>();
const selectedConnection = ref<{ id: string; name: string | null; job_title: string | null } | null>(null);
// Don't auto-open in track mode — the user clicks "Assign and track" instead
watch(
    () => props.goal?.id,
    () => {
        connectionOverlayOpen.value = !!props.goal?.team_member_goal && !props.goal?.connection;
    },
);

watch(connectionOverlayOpen, (open: boolean) => {
    if (!open) {
        selectedConnection.value = null;
        connectionAssignErrorText.value = "";
    }
});

const connectionPatch = ref<GoalUpdateRequest>({});
const { execute: patchConnection, error: patchConnectionError } = useFetch(
    computed(() => `/v1/goals/${props.goal?.id}`),
    { immediate: false },
)
    .patch(connectionPatch)
    .json<Goal>();

const isAssigningConnection = ref(false);
const connectionAssignErrorText = ref("");

async function submitConnectionAssign() {
    if (!selectedConnection.value) {
        return;
    }
    connectionPatch.value = {
        connection_id: selectedConnection.value.id,
        team_member_goal: true,
        confirm_goal: true,
    };
    isAssigningConnection.value = true;
    connectionAssignErrorText.value = "";
    await patchConnection();
    isAssigningConnection.value = false;

    if (patchConnectionError.value) {
        connectionAssignErrorText.value = "Couldn't assign this goal. Please try again.";
        return;
    }

    connectionOverlayOpen.value = false;
    logJourneysUserInteraction(
        JourneysAmplitudeEventNames.GOAL_CONNECTION_ASSIGNED,
        {
            goal_id: props.goal!.id,
            is_new_assignment: !props.goal!.connection,
        },
        props.currentTab,
    );
    emit("goal-updated", props.goal!.id);
}

const {
    data: chatData,
    isFetching: isFetchingChatUrl,
    execute: fetchChatUrl,
} = useFetch(
    computed(() => `/goals/${props.goal?.id}/chat/`),
    { immediate: false },
).json<{ coaching_session_url: string }>();

function handleMoreMenuOpened() {
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_MORE_MENU_OPENED, { goal_id: props.goal?.id }, props.currentTab);
}

const moreMenuSections = computed<DropdownMenuSection[]>(() => {
    const items: DropdownMenuItem[] = [];
    if (props.goal?.team_member_goal) {
        items.push({ id: "assign", type: "button", label: props.goal.connection ? "Reassign" : "Assign", icon: "bi bi-person" });
    }
    items.push({ id: "edit", type: "button", label: "Edit", icon: "bi bi-pencil" }, { id: "complete", type: "button", label: "Complete", icon: "bi bi-check-circle" });
    return [
        { id: "actions", items },
        { id: "danger", items: [{ id: "delete", type: "button", label: "Delete", icon: "bi bi-trash" }] },
    ];
});

function handleMoreMenuSelect(item: DropdownMenuItem) {
    if (item.id === "assign") {
        connectionOverlayOpen.value = true;
    } else if (item.id === "edit") {
        handleEditClicked();
    } else if (item.id === "complete") {
        showCompleteConfirm.value = true;
    } else if (item.id === "delete") {
        showDeleteFeedback.value = true;
    }
}

// While the menu is still open its focus trap swallows ConnectionSearch's own auto-focus, and on
// close reka hands focus back to the trigger. Skip that and focus the search box once the trap
// is gone; reka removes the focus scope in a timer after this event.
function handleMoreMenuCloseAutoFocus(event: Event) {
    if (!connectionOverlayOpen.value) {
        return;
    }
    event.preventDefault();
    globalThis.setTimeout(() => connectionSearchRef.value?.focus(), 0);
}

function handleEditClicked() {
    logJourneysUserInteraction(JourneysAmplitudeEventNames.GOAL_EDIT_CLICKED, { goal_id: props.goal!.id }, props.currentTab);
    emit("edit", props.goal!.id);
}

async function goToChat() {
    const discussData: DiscussWithNadiaClickedExtraData = { goal_id: props.goal?.id, source: "sidebar" };
    logJourneysUserInteraction(JourneysAmplitudeEventNames.DISCUSS_WITH_NADIA_CLICKED, discussData, props.currentTab);
    await fetchChatUrl();
    if (chatData.value?.coaching_session_url) {
        globalThis.location.href = chatData.value.coaching_session_url;
    }
}

const showPriorityButton = computed(() => !props.hidePriorityButton && !props.goal?.team_member_goal && !props.goal?.completed_at);
const isPriorityButtonDisabled = computed(() => !props.goal?.prioritized_at && !props.canPrioritize);

const fullDateLabel = computed(() =>
    props.goal
        ? new Date(props.goal.reference_session_created_at ?? props.goal.created_at).toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
          })
        : "",
);

const completedAtLabel = computed(() =>
    props.goal?.completed_at
        ? new Date(props.goal.completed_at).toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
          })
        : "",
);
</script>

<style scoped>
.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
    transform: translateY(100%);
}

@media (min-width: 768px) {
    .sidebar-slide-enter-from,
    .sidebar-slide-leave-to {
        transform: translateX(100%);
    }
}

.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
    transition: transform 300ms ease;
}

.sidebar-fade-enter-from,
.sidebar-fade-leave-to {
    opacity: 0;
}

.sidebar-fade-enter-active,
.sidebar-fade-leave-active {
    transition: opacity 200ms ease;
}
</style>
